import { ClientReservation, ContactMethod, ContactMethodName, Lawyer, Specialty } from '@/graphql/graphql';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '../store';

interface HomepageState {
  choices: {
    specialty: Specialty | null
    contactMethod: ContactMethod | null
    bookingTime: string | null
    clientReservation: ClientReservation | null
  },
  openModals: {
    specialty: boolean
    contactMethod: boolean
    bookingTime: boolean
  }
}
interface ISetOpenModalAction {
  modal: "specialty" | "contactMethod" | "bookingTime"
  isOpen: boolean
  sideEffect?: boolean
}
type ClientReservationKeys = keyof ClientReservation
interface IUpdateClientReservation {
  field: ClientReservationKeys
  data: any
}

type ExcludedSpecialtyTypes = "id" | "lawyers"
type RemoveLawyerFields<Type> = {
  [Property in keyof Type as Exclude<Property, ExcludedSpecialtyTypes>]: Type[Property];
};

type MappedLawyerSpecialties = {
  id: string;
  en: { name: string };
  ar: { name: string };
  specialties: Array<{
    ar: { title: string; description?: string | null };
    en: { title: string; description?: string | null };
  }>;
}
interface BookingTimePayload {
  utcBookingTime: string;
  chosenLawyer: MappedLawyerSpecialties
}

export const homepageSlice = createSlice({
  name: "homepage",
  initialState: {
    choices: {
      specialty: null,
      contactMethod: null,
      bookingTime: null,
      clientReservation: null
    },
    openModals: {
      specialty: false,
      contactMethod: false,
      bookingTime: false
    }
  } as HomepageState,
  reducers: {
    setSpecialty: (state, action: PayloadAction<Specialty>) => {
      state.choices.specialty = action.payload;
    },
    setContactMethod: (state, action: PayloadAction<ContactMethod>) => {
      state.choices.contactMethod = action.payload;
    },
    setBookingTime: (state, action: PayloadAction<BookingTimePayload>) => {
      state.choices.bookingTime = action.payload.utcBookingTime;
    },
    setOpenModal: (state, action: PayloadAction<ISetOpenModalAction>) => {
      state.openModals[action.payload.modal] = action.payload.isOpen;
    },
    setClientRservation: (state, action: PayloadAction<ClientReservation>) => {
      state.choices.clientReservation = action.payload
    },
    updateClientReservation: (state, action: PayloadAction<IUpdateClientReservation>) => {
      let { clientReservation } = state.choices
      if (clientReservation == null) {
        console.error('Client Reservation not initialized')
        return
      }
      clientReservation[action.payload.field] = action.payload.data
    }
  },
});

export const initializeClientReservationMiddleWare = (store: any) => (next: any) => (action: any) => {
  const targetActions = new Set(["homepage/setSpecialty", "homepage/setContactMethod", "homepage/setBookingTime"])
  const clientReservationState = store.getState().homepage.choices.clientReservation
  if (targetActions.has(action.type) && !clientReservationState) {
    const newReservation: ClientReservation = {
      clientId: new Date().getUTCMilliseconds().toString(),
      reservationId: new Date().getUTCSeconds().toString(),
      assignedLawyer: {
        id: '',
        ar: { name: '' },
        en: { name: '' },
        specialties: [],
        workingHours: {}
      },
      clientType: 'Individual',
      userType: 'Guest',
      clientName: 'Anonymous',
      chosenSpecialty: {
        id: '',
        ar: {title: ''},
        en: {title: ''},
        lawyers: [],
        legalOffices: []
      },
      chosenContactMethod: {
        id: ContactMethodName.PhoneCall,
        name: '',
        ar: { name: '' }
      },
      phoneNumber: null,
      whatsappNumber: null,
      reservationTime: null,
      reservationMessage: null
    }
    store.dispatch(setClientRservation(newReservation))
  }
  return next(action)
}

export const choicesMiddleWare = (store: any) => (next: any) => (action: any) => {
  if (action.type === "homepage/setSpecialty") {
    store.dispatch(updateClientReservation({ field: "chosenSpecialty", data: action.payload }))
  }
  if (action.type === "homepage/setContactMethod") {
    store.dispatch(updateClientReservation({ field: "chosenContactMethod", data: action.payload }))
  }
  if (action.type === "homepage/setBookingTime") {
    store.dispatch(updateClientReservation({ field: "reservationTime", data: action.payload.utcBookingTime }))
    store.dispatch(updateClientReservation({ field: "assignedLawyer", data: action.payload.chosenLawyer }))
  }
  return next(action);
};

export const openModalsMiddleWare = (store: any) => (next: any) => (action: any) => {
  if (action.type === "homepage/setOpenModal") {
    if (action.payload.sideEffect) {
      return next(action)
    }
    switch (action.payload.modal) {
      case 'specialty':
        store.dispatch(setOpenModal({ modal: "contactMethod", isOpen: false, sideEffect: true }))
        store.dispatch(setOpenModal({ modal: "bookingTime", isOpen: false, sideEffect: true }))
        break
      case 'contactMethod':
        store.dispatch(setOpenModal({ modal: "specialty", isOpen: false, sideEffect: true }))
        store.dispatch(setOpenModal({ modal: "bookingTime", isOpen: false, sideEffect: true }))
        break
      case 'bookingTime':
        store.dispatch(setOpenModal({ modal: "specialty", isOpen: false, sideEffect: true }))
        store.dispatch(setOpenModal({ modal: "contactMethod", isOpen: false, sideEffect: true }))
        break
      default:
        break
    }
  }
  next(action)
}

export const selectHomepage = (state: RootState) => state.homepage;

export const { setSpecialty, setContactMethod, setBookingTime, setOpenModal, setClientRservation, updateClientReservation } = homepageSlice.actions;
